/* eslint-disable react-refresh/only-export-components, @typescript-eslint/strict-boolean-expressions */

import React, { useRef, useState, createContext, useContext } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  ImperativePanelHandle,
} from 'react-resizable-panels';
import { DashboardLayoutType, ProductType } from 'types/shared';
import PanelFullscreenButton from 'components/shared/PanelFullscreenButton';
import PanelFoldButton from 'components/shared/PanelFoldButton';
import { HideSupportStyle } from 'components/shared/HideSupportStyle';

interface VerticalDashboardLayoutProps {
  dashboardId: ProductType;
  mainContent: React.ReactNode;
  topLeftContent: React.ReactNode;
  topRightContent?: React.ReactNode; // optional second component in top row
  bottomContent: React.ReactNode;
  bottomContentHeaderName: string;
  mainContentHeaderName: string;
  customDefaultSizes?: {
    top: number;
    main: number;
    bottom: number;
  };
}

interface CollapsedState {
  bottom: boolean;
  top: boolean;
  main: boolean;
}

export interface VerticalDashboardContextType {
  isMainFullscreen: boolean;
  isBottomFullscreen: boolean;
  isTopCollapsed: boolean;
  isBottomCollapsed: boolean;
  isMainCollapsed: boolean;
  toggleMainFullscreen: () => void;
  toggleBottomFullscreen: () => void;
  toggleExpandBottom: () => void;
  toggleExpandTop: () => void;
  toggleExpandMain: () => void;
  bottomContentHeaderName: string;
  mainContentHeaderName: string;
}

const VerticalDashboardContext =
  createContext<VerticalDashboardContextType | null>(null);

export const useVerticalDashboardFullscreen =
  (): VerticalDashboardContextType => {
    const context = useContext(VerticalDashboardContext);
    if (!context) {
      throw new Error(
        'useFullscreen must be used within VerticalDashboardLayout',
      );
    }
    return context;
  };

export const VerticalDashboardLayout: React.FC<
  VerticalDashboardLayoutProps
> = ({
  dashboardId,
  mainContent,
  topLeftContent,
  topRightContent,
  bottomContent,
  bottomContentHeaderName,
  mainContentHeaderName,
  customDefaultSizes,
}) => {
  const theme = useTheme();
  const isLgDown = useIsMobile();

  // Panel refs for manipulation
  const topPanelRef = useRef<ImperativePanelHandle>(null);
  const mainPanelRef = useRef<ImperativePanelHandle>(null);
  const bottomPanelRef = useRef<ImperativePanelHandle>(null);

  // Track collapsed states
  const [collapsed, setCollapsed] = useState<CollapsedState>({
    bottom: false,
    top: false,
    main: false,
  });

  // Store previous states before entering fullscreen or collapsing
  const previousStates = useRef<CollapsedState | null>(null);

  // Fullscreen control functions
  const toggleMainFullscreen = () => {
    const isFullscreen = collapsed.top && collapsed.bottom;

    if (!isFullscreen) {
      // Store current states before entering fullscreen
      previousStates.current = { ...collapsed };

      // Enter fullscreen
      topPanelRef.current?.collapse();
      bottomPanelRef.current?.collapse();
      setCollapsed((prev) => ({ ...prev, top: true, bottom: true }));
    } else {
      // Restore previous states
      if (previousStates.current) {
        if (!previousStates.current.top) {
          topPanelRef.current?.expand();
        }
        if (!previousStates.current.bottom) {
          bottomPanelRef.current?.expand();
        }
        setCollapsed(previousStates.current);
      }
    }
  };

  const toggleBottomFullscreen = () => {
    const isFullscreen = collapsed.top && collapsed.main;

    if (!isFullscreen) {
      previousStates.current = { ...collapsed };
      topPanelRef.current?.collapse();
      mainPanelRef.current?.collapse();
      setCollapsed((prev) => ({ ...prev, top: true, main: true }));
    } else {
      if (previousStates.current) {
        if (!previousStates.current.top) {
          topPanelRef.current?.expand();
        }
        if (!previousStates.current.main) {
          mainPanelRef.current?.expand();
        }
        setCollapsed(previousStates.current);
      }
    }
  };

  const toggleExpandBottom = () => {
    if (collapsed.bottom) {
      bottomPanelRef.current?.expand();
      setCollapsed((prev) => ({ ...prev, bottom: false }));
    } else {
      bottomPanelRef.current?.collapse();
      setCollapsed((prev) => ({ ...prev, bottom: true }));
    }
  };

  const toggleExpandTop = () => {
    if (collapsed.top) {
      topPanelRef.current?.expand();
      setCollapsed((prev) => ({ ...prev, top: false }));
    } else {
      topPanelRef.current?.collapse();
      setCollapsed((prev) => ({ ...prev, top: true }));
    }
  };

  const toggleExpandMain = () => {
    if (collapsed.main) {
      mainPanelRef.current?.expand();
      setCollapsed((prev) => ({ ...prev, main: false }));
    } else {
      mainPanelRef.current?.collapse();
      setCollapsed((prev) => ({ ...prev, main: true }));
    }
  };

  const contextValue: VerticalDashboardContextType = {
    isMainFullscreen: collapsed.top && collapsed.bottom,
    isBottomFullscreen: collapsed.top && collapsed.main,
    isBottomCollapsed: collapsed.bottom,
    isTopCollapsed: collapsed.top,
    isMainCollapsed: collapsed.main,
    toggleMainFullscreen,
    toggleBottomFullscreen,
    toggleExpandBottom,
    toggleExpandTop,
    toggleExpandMain,
    bottomContentHeaderName,
    mainContentHeaderName,
  };

  const anyPanelIsFullscreen =
    (collapsed.top && collapsed.bottom) || // main fullscreen
    (collapsed.top && collapsed.main); // bottom fullscreen

  return (
    <>
      {anyPanelIsFullscreen && <HideSupportStyle />}

      <VerticalDashboardContext.Provider value={contextValue}>
        {isLgDown ? (
          <Stack sx={{ gap: 6, width: '100%' }}>
            <Stack sx={{ gap: 3 }}>
              <Box
                sx={{
                  height: '100%',
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  p: '8px',
                }}
              >
                {topLeftContent}
              </Box>
              {topRightContent && (
                <Box
                  sx={{
                    height: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: '8px',
                  }}
                >
                  {topRightContent}
                </Box>
              )}
            </Stack>
            <Box
              sx={{
                height: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: '8px',
              }}
            >
              {mainContent}
            </Box>
            <Box
              sx={{
                height: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: '8px',
              }}
            >
              {bottomContent}
            </Box>
          </Stack>
        ) : (
          <Box sx={{ minHeight: '100%', paddingBottom: '20px' }}>
            <PanelGroup
              direction="vertical"
              autoSaveId={`${dashboardId}-vertical`}
            >
              <Panel
                ref={topPanelRef}
                collapsible
                defaultSize={customDefaultSizes?.top ?? 20}
                minSize={15}
                onCollapse={() =>
                  setCollapsed((prev) => ({ ...prev, top: true }))
                }
                onExpand={() =>
                  setCollapsed((prev) => ({ ...prev, top: false }))
                }
              >
                {topRightContent ? (
                  <PanelGroup
                    direction="horizontal"
                    autoSaveId={`${dashboardId}-top-horizontal`}
                  >
                    <Panel defaultSize={50} minSize={20} collapsible>
                      <Box
                        sx={{
                          height: '100%',
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          p: '12px',
                        }}
                      >
                        {topLeftContent}
                      </Box>
                    </Panel>
                    <PanelResizeHandle style={{ padding: 0 }}>
                      <Stack
                        sx={{
                          flexDirection: 'column',
                          gap: 2,
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: '2px',
                            height: '100%',
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                          }}
                        />
                        <IconButton color="primary" size="small">
                          <DragHandleIcon sx={{ transform: 'rotate(90deg)' }} />
                        </IconButton>
                        <Box
                          sx={{
                            width: '2px',
                            height: '100%',
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                          }}
                        />
                      </Stack>
                    </PanelResizeHandle>
                    <Panel defaultSize={50} minSize={20} collapsible>
                      <Box
                        sx={{
                          height: '100%',
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          p: '12px',
                        }}
                      >
                        {topRightContent}
                      </Box>
                    </Panel>
                  </PanelGroup>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      p: '12px',
                    }}
                  >
                    {topLeftContent}
                  </Box>
                )}
              </Panel>

              <PanelResizeHandle style={{ padding: 0 }}>
                <Stack
                  sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '2px',
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    }}
                  />
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={toggleExpandTop}
                  >
                    <DragHandleIcon />
                  </IconButton>
                  <Box
                    sx={{
                      width: '100%',
                      height: '2px',
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    }}
                  />
                </Stack>
              </PanelResizeHandle>

              {collapsed.main && (
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: '16px',
                  }}
                >
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      gap: 2,
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 12, md: 14 },
                        fontWeight: 600,
                      }}
                    >
                      {mainContentHeaderName}
                    </Typography>
                    <Stack sx={{ flexDirection: 'row', gap: 2 }}>
                      <PanelFullscreenButton
                        panelType="main"
                        layoutType={DashboardLayoutType.RESIZABLE_VERTICAL}
                      />
                      <PanelFoldButton
                        panelType="main"
                        layoutType={DashboardLayoutType.RESIZABLE_VERTICAL}
                      />
                    </Stack>
                  </Stack>
                </Box>
              )}

              <Panel
                ref={mainPanelRef}
                defaultSize={customDefaultSizes?.main ?? 50}
                minSize={30}
                collapsible
                onCollapse={() =>
                  setCollapsed((prev) => ({ ...prev, main: true }))
                }
                onExpand={() =>
                  setCollapsed((prev) => ({ ...prev, main: false }))
                }
              >
                <Box
                  sx={{
                    height: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: '12px',
                  }}
                >
                  {mainContent}
                </Box>
              </Panel>

              <PanelResizeHandle style={{ padding: 0 }}>
                <Stack
                  sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '2px',
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    }}
                  />
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={toggleExpandBottom}
                  >
                    <DragHandleIcon />
                  </IconButton>
                  <Box
                    sx={{
                      width: '100%',
                      height: '2px',
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    }}
                  />
                </Stack>
              </PanelResizeHandle>

              {collapsed.bottom && (
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: '16px',
                  }}
                >
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      gap: 2,
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 12, md: 14 },
                        fontWeight: 600,
                      }}
                    >
                      {bottomContentHeaderName}
                    </Typography>
                    <Stack sx={{ flexDirection: 'row', gap: 2 }}>
                      <PanelFullscreenButton
                        panelType="bottom"
                        layoutType={DashboardLayoutType.RESIZABLE_VERTICAL}
                      />
                      <PanelFoldButton
                        panelType="bottom"
                        layoutType={DashboardLayoutType.RESIZABLE_VERTICAL}
                      />
                    </Stack>
                  </Stack>
                </Box>
              )}

              <Panel
                ref={bottomPanelRef}
                defaultSize={customDefaultSizes?.bottom ?? 30}
                minSize={15}
                collapsible
                onCollapse={() =>
                  setCollapsed((prev) => ({ ...prev, bottom: true }))
                }
                onExpand={() =>
                  setCollapsed((prev) => ({ ...prev, bottom: false }))
                }
              >
                <Box
                  sx={{
                    height: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: '12px',
                    overflow: 'auto',
                  }}
                >
                  {bottomContent}
                </Box>
              </Panel>
            </PanelGroup>
          </Box>
        )}
      </VerticalDashboardContext.Provider>
    </>
  );
};
